import { useEffect, useRef } from "react";
import { useToaster, useToasterStore } from "react-hot-toast";

/* react-hot-toast pauses EVERY toast's auto-dismiss timer while the pointer is
 * over a toast, and resumes on the toaster's mouseleave. When a toast is
 * closed under the pointer -- which is how every X press works -- the browser
 * never reports a leave for an element that no longer exists, so the library
 * stays paused for the rest of the session: no later toast ever times out, and
 * popups sit on screen for hours. The owner reported exactly that, repeatedly.
 *
 * This guard watches the library's own `pausedAt`. While a pause is in force
 * it ends it as soon as the pointer is demonstrably not over a toast (the
 * next pointer move or touch lands outside the toaster), or as soon as no
 * visible toast is left to hover. It renders nothing. */

export const TOASTER_CLASS = "uniflo-toaster";
export const TOASTER_OPTIONS = { duration: 5000 };

export default function ToastPauseGuard() {
  const { pausedAt, toasts } = useToasterStore();
  // Same options as the mounted <Toaster>, so this second subscriber can never
  // shorten a toast's life.
  const { handlers } = useToaster(TOASTER_OPTIONS);
  const endPauseRef = useRef(handlers.endPause);
  endPauseRef.current = handlers.endPause;

  const visibleCount = toasts.filter((item) => item.visible).length;

  useEffect(() => {
    if (!pausedAt) return undefined;
    if (visibleCount === 0) {
      endPauseRef.current();
      return undefined;
    }
    const endPauseWhenOutside = (event) => {
      const target = event.target;
      const overToast =
        target && typeof target.closest === "function" && target.closest(`.${TOASTER_CLASS}`);
      if (!overToast) endPauseRef.current();
    };
    document.addEventListener("mousemove", endPauseWhenOutside, true);
    document.addEventListener("touchstart", endPauseWhenOutside, true);
    return () => {
      document.removeEventListener("mousemove", endPauseWhenOutside, true);
      document.removeEventListener("touchstart", endPauseWhenOutside, true);
    };
  }, [pausedAt, visibleCount]);

  return null;
}
